import { Grid, List, ListItem, Theme, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import { get as _get } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { createRentalService } from '../../../api/createServiceApi';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import { getAuthKey, getClaimNumber, getSessionId, getUrlKey } from '../../../app/store/combinedSelectors';
import * as ContextRouterActions from '../../../app/store/ContextRouter/ContextRouterActions';
import {
  updateSelectedProvidersState,
  updateStageState,
} from '../../../app/store/EligibilityService/EligibilityServiceSlice';
import { useActions } from '../../../app/store/useActions';
import { backBtn, confirmReservationBtn, goToClaimsBtn } from '../../../Constants/Constants';
import { CreateRentalRequest } from '../../../model/create-auto-service-request';
import { CreateServiceResponse, IconStateType, ProviderInfo, StageNames } from '../../../types/type';
import { getStagesInOrder } from '../../../utils/Utilities';
import CallToAction from '../../shared/CallToAction/CallToAction';
import ConfirmationHeader from '../../shared/ConfirmationHeader/ConfirmationHeader';
import ContactCard from '../../shared/ContactCard/ContactCard';
import ModalLinearActivityIndicator from '../../shared/ModalLinearActivityIndicator/ModalLinearActivityIndicator';
import StepProgressBar from '../../shared/StepProgressBar/StepProgressBar';

interface SelectedProvider {
  provider: ProviderInfo;
}
export default function RentalServicesFinalize() {
  const theme: Theme = useTheme();
  const navigate = useNavigate();
  const availableProgressStages = useAppSelector((state) => state.services.availableProgressStages);
  const currentStage = availableProgressStages.filter((stage) => stage.viewActive === true);

  // Get the rest of stages excluding the current stage. Find the next stage which is valid
  const nextStage = getStagesInOrder(
    availableProgressStages.filter(
      (stage) => stage.id !== currentStage[0].id && !stage.completed && stage.isEligible && !stage.skipped
    )
  )[0];

  const claimNumber = useSelector(getClaimNumber);
  const urlKey = useSelector(getUrlKey);
  const sessionId = useSelector(getSessionId);
  const authKey = useSelector(getAuthKey);
  const [modalLoadingOpen, setModalLoadingOpen] = React.useState(false);
  const [serviceError, setServiceError] = React.useState(false);
  const { state } = useLocation();

  const { provider } = state as SelectedProvider;

  const vin = useAppSelector((state) => _get(state, 'claimDetail.claimSummary.autoClaimDetails[0].vin', ''));
  const contextRouterActions: typeof ContextRouterActions = useActions(ContextRouterActions);
  const dispatch = useAppDispatch();
  const nextStepsText =
    'You will receive further communication on your claim as it progresses. Please take reasonable steps to secure your property and prevent further damage.';
  const reminderText = 'You can always return to the available property service(s) by viewing your claim’s details.';
  const headerText = 'Finalize Reservation';
  const subHeaderText = "You'll pick up your rental from:";

  async function goToClaimsOverview() {
    navigate('/claims-overview', {
      state: {
        isClaimRefreshNeeded: true,
      },
    });
  }

  async function confirmReservation() {
    setModalLoadingOpen(true);

    // Make web service call to create a service
    const response = await selectService(provider);
    // Hide LoadingIndicator
    setModalLoadingOpen(false);
    if (response) {
      // Once success, update the local store
      updateLocalStore();

      // Go to the next screen
      gotoNextScreen(provider);
    } else {
      //Show UI for service error handling
      setServiceError(true);
    }
  }

  async function selectService(selectedService: ProviderInfo) {
    // Show LoadingIndicator
    setModalLoadingOpen(true);
    const postBody: CreateRentalRequest = {
      claimSR: {
        vin: vin,
        rentalLocPicked: _get(selectedService, 'providerID', ''),
      },
    };
    const response: CreateServiceResponse = await createRentalService(
      urlKey,
      sessionId,
      authKey,
      claimNumber,
      postBody
    );
    if ((_get(response, 'response.status.code') as unknown as number) === 200) {
      if (_get(response, 'response.updatedAuthToken')) {
        contextRouterActions.updateAuthToken(response.response.updatedAuthToken);
      }
    }

    return response;
  }

  function updateLocalStore() {
    // Update provider's information in local store too, since the remote update was success.
    const selectedProvider: ProviderInfo = {
      serviceTypeName: StageNames.RENTAL,
      serviceAvailable: '',
      providerName: '',
      providerID: '',
      providerPhone: '',
      providerUrl: undefined,
      subServiceTypes: undefined,
    };
    dispatch(
      updateSelectedProvidersState({
        selectedProvider: selectedProvider,
      })
    );
    //Update the current stage state and prepare the next stage state.
    const updatedStages = availableProgressStages.map((stage) => {
      stage =
        stage.id === currentStage[0].id
          ? {
              ...stage,
              completed: true,
              viewActive: true,
              showProgress: false,
              skipped: false,
            }
          : stage;
      return stage;
    });
    dispatch(updateStageState({ updatedStages: updatedStages }));
  }

  function gotoNextScreen(selectedProviderInfo: ProviderInfo) {
    navigate('/auto-service-confirmation', {
      state: {
        provider: selectedProviderInfo,
      },
    });
  }

  const contentWhenServicesSelected = (
    <>
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
        }}
      >
        <Grid item xs={12}>
          <ConfirmationHeader
            headerContent={headerText}
            subHeaderContent={subHeaderText}
            state={IconStateType.NONE}
          ></ConfirmationHeader>
        </Grid>
        <Grid item xs={12}>
          <ContactCard isAuto={true} providerInfo={provider}></ContactCard>
        </Grid>
      </Grid>
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
        }}
      >
        <Grid item xs={12}>
          <Typography
            sx={{
              ...theme.custom.styles.serviceConfirmationHeader,
              marginBottom: '1rem',
              marginTop: '1rem',
              scrollSnapMarginTop: '2rem',
            }}
          >
            {'Next Steps'}
          </Typography>
          <Typography>
            {
              'A representative from Enterprise Rent-A-Car will contact you to finalize the details about the type of vehicle and when you’ll pick it up.'
            }
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography
            sx={{
              ...theme.custom.styles.serviceConfirmationHeader,
              marginBottom: '1rem',
              marginTop: '2rem',
              scrollSnapMarginTop: '2rem',
            }}
          >
            {'Important Things to Know'}
          </Typography>
        </Grid>
      </Grid>
      <Grid
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
        }}
        item
        xs={12}
      >
        <List sx={{ ...theme.custom.styles.confirmationList, listStyle: 'number' }}>
          <ListItem sx={theme.custom.styles.confirmationListElement}>
            <Typography variant="body1">
              <b>We only provide coverage for the days your vehicle is being repaired.</b>
            </Typography>
          </ListItem>
          <ListItem sx={theme.custom.styles.confirmationListElement}>
            <Typography variant="body1">
              <b>Bring your driver's license and proof of insurance to the rental office.</b> This is required to rent
              your vehicle.
            </Typography>
          </ListItem>

          <ListItem sx={theme.custom.styles.confirmationListElement}>
            <Typography variant="body1">
              <b>Prepare to put a deposit on a credit or debit card when you pick up your rental.</b> The rental company
              may require this to cover any incidentals.
            </Typography>
          </ListItem>

          <ListItem sx={theme.custom.styles.confirmationListElement}>
            <Typography variant="body1">
              <b>We do not cover gas, upgrades, additional mileage or any optional insurance coverage.</b> You are
              responsible for the charges.
            </Typography>
          </ListItem>

          <ListItem sx={theme.custom.styles.confirmationListElement}>
            <Typography variant="body1">
              <b>You can upgrade your vehicle, but you'll be responsible for the difference in cost.</b> Ask your
              Enterprise representative for more information.
            </Typography>
          </ListItem>
        </List>
      </Grid>
    </>
  );

  return (
    <>
      <StepProgressBar stateInfo={availableProgressStages}></StepProgressBar>
      {contentWhenServicesSelected}
      {modalLoadingOpen && <ModalLinearActivityIndicator />}
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
          marginTop: '0em',
        }}
      >
        <Grid item xs={12}>
          <CallToAction
            nextButton={true}
            nextButtonContent={confirmReservationBtn}
            handleNext={() => confirmReservation()}
            backButton={true}
            backButtonContent={backBtn}
            handleBack={() => navigate(-1)}
            skipButton={false}
            cancelButton={true}
            cancelButtonContent={goToClaimsBtn}
            handleCancel={goToClaimsOverview}
          ></CallToAction>
        </Grid>
      </Grid>
    </>
  );
}
